/**
 * /maintenance + /maintenance/:id - operator surface for OCP-60's
 * scheduled maintenance windows (OCP-485, Phase E).
 *
 * List page: every window + status, a status filter, and the create form
 * (datepicker + target-version + markdown description + a live eligible-vs-
 * pinned scope preview). Per OCP-60 the notification email carries only the
 * operator's markdown - the version number is deliberately NOT in the body -
 * so the description field says exactly what customers see.
 *
 * Detail page: the per-tenant `tenant_maintenance_event` breakdown with live
 * progress polling (pending -> running -> terminal), the cancel action
 * (pending only), and the two-step Confirm-done action that fires the all-clear
 * mail once the operator has had their hotfix buffer.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { CoolifyTokenHealthCard } from '../components/CoolifyTokenHealthCard';
import { GeoIpDatabaseCard } from '../components/GeoIpDatabaseCard';
import { MailRenderPreview, type RenderedMailParts } from '../components/MailRenderPreview';
import { api, ApiError, readErrorKey } from '../lib/api';
import { DISPLAY_TIME_ZONE, datetimeLocalToIso, fmtDateTime, fmtTime, isoToDatetimeLocal, zoneLabel } from '../lib/format-time';

type WindowStatus =
  | 'pending'
  | 'running'
  | 'completed_pending_confirm'
  | 'completed'
  | 'cancelled';

// OCP-870 - skipped_deploy_in_flight: not rolled, another deploy of the tenant was in flight.
type EventStatus = 'skipped_pinned' | 'notified' | 'rolling' | 'completed' | 'failed' | 'skipped_deploy_in_flight';

const EVENT_STATUS_LABEL: Record<EventStatus, string> = {
  skipped_pinned: 'skipped pinned',
  notified: 'notified',
  rolling: 'rolling',
  completed: 'completed',
  failed: 'failed',
  skipped_deploy_in_flight: 'skipped, deploy in flight',
};

// OCP-527: the AGB V4 5.2 classification of a window. Only `routine` is
// constrained by the contractual windows + notice period.
type MaintenanceKind = 'routine' | 'integrity' | 'non_interrupting';

const KIND_LABEL: Record<MaintenanceKind, string> = {
  routine: 'Routine',
  integrity: 'Integrity',
  non_interrupting: 'Non-interrupting',
};

const KIND_HELP: Record<MaintenanceKind, string> = {
  routine:
    'Materially interrupts the service. Must sit inside a contractual maintenance window - AGB V4 5.2: Sat 08:00 to Sun 22:00 and weekday nights 23:00 to 09:00 (Europe/Berlin), as configured in maintenance.windows. No individual advance notice is owed inside a window, so a short lead is fine. This is the default and the only kind that is enforced.',
  integrity:
    'Averts a danger to system security or integrity, or fixes an error that materially impairs use (AGB V4 5.2). May run outside the windows and without notice, but the reason is mandatory, audited, and disclosed to the customer by e-mail. This is the path for daytime work that cannot wait for the next window - not a way around a scheduling constraint.',
  non_interrupting:
    'Work that does not, or only insignificantly, impairs use - in particular a rolling update, instance by instance with an automatic function check (AGB V4 5.2). Allowed at any time without notice.',
};

interface PolicySlot {
  days: string[];
  start: string;
  end: string;
  // OCP-537: how far past its opening day the slot runs (1 for the weekend
  // window, which crosses Saturday midnight), and whether a routine window
  // inside it owes the contractual advance notice at all.
  spans_days?: number;
  requires_notice?: boolean;
  label: string;
}

interface MaintenancePolicyView {
  timezone: string;
  min_notice_hours: number;
  windows: PolicySlot[];
  next_compliant_at?: string | null;
}

/** Shape of the 400 the create route returns when AGB V4 5.2 is violated. */
interface PolicyRejection {
  error?: string;
  violations?: string[];
  message?: string;
  policy?: MaintenancePolicyView;
  earliest_notice_at?: string;
  next_compliant_at?: string | null;
}

interface WindowCounts {
  total: number;
  skipped_pinned: number;
  notified: number;
  rolling: number;
  completed: number;
  failed: number;
  /** OCP-870 - absent on an api older than the status. */
  skipped_deploy_in_flight?: number;
}

interface MaintenanceWindow {
  id: string;
  scheduled_at: string;
  description_md: string;
  status: WindowStatus;
  target_image_tag: string | null;
  // OCP-527: AGB V4 5.2 classification + the mandatory integrity reason.
  kind?: MaintenanceKind;
  kind_reason?: string | null;
  // OCP-505: 'operator' (this admin flow) or 'auto' (autonomous cycle).
  origin?: 'operator' | 'auto';
  vps_server_id?: string | null;
  created_by_email: string;
  notification_sent_at: string | null;
  all_clear_sent_at: string | null;
  created_at: string;
  updated_at: string;
  counts?: WindowCounts;
}

// OCP-505: autonomous fleet-maintenance cycle status (read-only panel).
// OCP-508: adds the Phase-B tenant-version-convergence config + last run.
interface AutoMaintenanceStatus {
  enabled: boolean;
  schedule: string;
  /** OCP-765 - the zone the cron is evaluated in (wall clock). */
  schedule_timezone?: string;
  window_lead_minutes: number;
  max_reboots_per_run: number;
  patch_fleet_concurrency: number;
  update_tenants: boolean;
  max_tenant_updates_per_run: number;
  canary_subdomains: string[];
  /** OCP-855 - the release Phase B rolls onto; null = none verified (no roll). */
  verified_core_release?: string | null;
  next_run_at: string | null;
  last_run: { at: string; summary: unknown } | null;
  last_convergence: { at: string; summary: unknown } | null;
  recent_windows: Array<{
    id: string;
    status: string;
    hostname: string | null;
    scheduled_at: string | null;
    all_clear_sent_at: string | null;
    created_at: string | null;
  }>;
}

interface TenantEvent {
  id: string;
  tenant_id: string;
  subdomain: string | null;
  status: EventStatus;
  started_at: string | null;
  completed_at: string | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
  /** OCP-593 - the locale this tenant's notice / all-clear is rendered in, and whether that is the en fallback. */
  mail_locale?: 'de' | 'en';
  mail_locale_fallback?: boolean;
}

/** OCP-593 - per-window locale summary; `fallback` names who got English for lack of a template. */
interface MailLocales {
  de: number;
  en: number;
  fallback: Array<{ tenant_id: string; subdomain: string | null; locale: string }>;
}

interface ScopePreview {
  rolling_image_tag: string;
  // OCP-527: the live contractual policy, so the form can show the operator
  // what AGB V4 5.2 allows before they pick a time.
  policy?: MaintenancePolicyView;
  eligible: { count: number; tenants: Array<{ id: string; subdomain: string }> };
  pinned: { count: number; tenants: Array<{ id: string; subdomain: string }> };
}

const STATUS_LABEL: Record<WindowStatus, string> = {
  pending: 'Pending',
  running: 'Running',
  completed_pending_confirm: 'Awaiting confirm',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

// ---- list page -----------------------------------------------------

export function AdminMaintenancePage() {
  const [items, setItems] = useState<MaintenanceWindow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<WindowStatus | ''>('');

  const load = useCallback(async () => {
    setError(null);
    try {
      const qs = statusFilter ? `?status=${statusFilter}` : '';
      const r = await api<{ items: MaintenanceWindow[] }>(`/v1/admin/maintenance-windows${qs}`);
      setItems(r.items);
    } catch (err) {
      setError(err instanceof ApiError ? `Could not load windows (HTTP ${err.status}).` : 'Could not load windows.');
    }
  }, [statusFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="mx-auto max-w-5xl">
      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Maintenance windows</h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Schedule a fleet update: notify tenants, roll every latest-channel tenant onto the
            target version (pinned tenants skipped), then confirm-done to send the all-clear.
          </p>
        </div>
      </div>

      {/* OCP-588 - a dead panel token burns every roll; show it before the form. */}
      <CoolifyTokenHealthCard />

      {/* OCP-848 - the country source of the DNS-only hosts; its alert links here. */}
      <GeoIpDatabaseCard />

      <AutoMaintenancePanel />

      <CreateWindowForm onCreated={() => void load()} />

      <div className="mt-10 flex items-center justify-between">
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Windows</h2>
        <label className="text-sm">
          <span className="sr-only">Filter by status</span>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as WindowStatus | '')}
            aria-label="Filter by status"
            className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1 text-sm"
          >
            <option value="">All statuses</option>
            {(Object.keys(STATUS_LABEL) as WindowStatus[]).map((s) => (
              <option key={s} value={s}>
                {STATUS_LABEL[s]}
              </option>
            ))}
          </select>
        </label>
      </div>

      {error && (
        <div
          role="alert"
          className="mt-3 rounded border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300"
        >
          {error}
        </div>
      )}

      {items === null && !error ? (
        <div className="mt-4 rounded-lg border border-dashed border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 p-6 text-sm text-gray-500">
          Loading…
        </div>
      ) : items && items.length === 0 ? (
        <div className="mt-4 rounded-lg border border-dashed border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 p-6 text-sm text-gray-500">
          No maintenance windows yet. Schedule one above to roll the fleet onto a new version.
        </div>
      ) : items ? (
        <div className="mt-4 overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-3 py-2">Scheduled</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Kind</th>
                <th className="px-3 py-2">Target</th>
                <th className="px-3 py-2">Progress</th>
                <th className="px-3 py-2">Created by</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {items.map((w) => (
                <tr key={w.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/60">
                  <td className="whitespace-nowrap px-3 py-2 tabular-nums text-gray-700 dark:text-gray-300">
                    {fmtDateTime(w.scheduled_at)}
                  </td>
                  <td className="px-3 py-2">
                    <StatusPill status={w.status} />
                  </td>
                  <td className="px-3 py-2">
                    <KindPill kind={w.kind ?? 'routine'} />
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-gray-600 dark:text-gray-400">
                    {w.target_image_tag ?? '-'}
                  </td>
                  <td className="px-3 py-2 text-gray-600 dark:text-gray-400">
                    {w.counts ? <ProgressSummary counts={w.counts} /> : '-'}
                  </td>
                  <td className="px-3 py-2 text-gray-600 dark:text-gray-400">
                    {w.origin === 'auto' ? (
                      <span
                        title="Opened by the autonomous fleet-maintenance cycle"
                        className="inline-flex items-center rounded bg-indigo-50 dark:bg-indigo-900/30 px-1.5 py-0.5 text-xs font-semibold text-indigo-700 dark:text-indigo-300"
                      >
                        auto
                      </span>
                    ) : (
                      w.created_by_email
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Link
                      to={`/maintenance/${w.id}`}
                      className="text-sm font-medium text-brand-500 hover:text-brand-600"
                    >
                      Details →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}

/**
 * OCP-536 - the picker speaks the POLICY zone (`maintenance.timezone`,
 * default Europe/Berlin), in both directions: an ISO instant is shown as
 * that zone's wall clock, and the wall clock the operator types is
 * converted back with the offset valid on that day. Input and display use
 * the same zone, so a scheduled instant round-trips unchanged - and the
 * result no longer depends on which OS clock the operator's browser sits on.
 */
function toDatetimeLocalValue(iso: string, timeZone: string): string {
  return isoToDatetimeLocal(iso, timeZone);
}

/** OCP-928 - `POST /v1/admin/maintenance-windows/mail-preview`. */
interface MaintenanceMailPreview {
  tenant_url: string;
  per_tenant_url: boolean;
  pre_notify: { de: RenderedMailParts; en: RenderedMailParts };
  all_clear: { de: RenderedMailParts; en: RenderedMailParts };
}

function CreateWindowForm({ onCreated }: { onCreated: () => void }) {
  const navigate = useNavigate();
  const [scheduledAt, setScheduledAt] = useState('');
  const [targetTag, setTargetTag] = useState('');
  const [description, setDescription] = useState('');
  const [kind, setKind] = useState<MaintenanceKind>('routine');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rejection, setRejection] = useState<PolicyRejection | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [scope, setScope] = useState<ScopePreview | null>(null);
  const [mailPreview, setMailPreview] = useState<MaintenanceMailPreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  // OCP-536 - the zone the picker and every rendered time on this page use.
  const policyZone = scope?.policy?.timezone ?? DISPLAY_TIME_ZONE;

  // A mail preview belongs to exactly the inputs it was rendered from.
  useEffect(() => {
    setMailPreview(null);
  }, [description, kind, reason, scheduledAt]);

  // OCP-928 - the pre-notify + all-clear mails exactly as the workers render
  // them (server side, same templates), shown in sandboxed frames.
  const previewMails = useCallback(async () => {
    setPreviewing(true);
    setError(null);
    try {
      const iso = scheduledAt ? (datetimeLocalToIso(scheduledAt, policyZone) ?? new Date(scheduledAt).toISOString()) : undefined;
      setMailPreview(
        await api<MaintenanceMailPreview>('/v1/admin/maintenance-windows/mail-preview', {
          method: 'POST',
          body: {
            description_md: description,
            kind,
            ...(reason.trim() ? { reason: reason.trim() } : {}),
            ...(iso ? { scheduled_at: iso } : {}),
          },
        }),
      );
    } catch (err) {
      setMailPreview(null);
      setError(err instanceof ApiError ? `Could not render the mail preview (HTTP ${err.status}).` : 'Could not render the mail preview.');
    } finally {
      setPreviewing(false);
    }
  }, [description, kind, reason, scheduledAt, policyZone]);

  useEffect(() => {
    void (async () => {
      try {
        const r = await api<ScopePreview>('/v1/admin/maintenance-windows/scope-preview');
        setScope(r);
      } catch {
        // Scope preview is a nicety - a failure shouldn't block the form.
      }
    })();
  }, []);

  // An integrity window may skip the contractual window + notice, so the
  // justification is not optional - mirror the server-side rule in the form.
  const reasonRequired = kind === 'integrity';
  const canSubmit =
    scheduledAt !== '' &&
    description.trim() !== '' &&
    (!reasonRequired || reason.trim() !== '') &&
    !submitting;

  const submit = useCallback(async () => {
    setSubmitting(true);
    setError(null);
    setRejection(null);
    setWarnings([]);
    try {
      const r = await api<{ window: MaintenanceWindow; warnings: string[] }>(
        '/v1/admin/maintenance-windows',
        {
          method: 'POST',
          body: {
            // OCP-536: the picker value is POLICY-zone wall clock; convert with
            // that zone's offset for that day (never the browser's OS clock).
            scheduled_at: datetimeLocalToIso(scheduledAt, policyZone) ?? new Date(scheduledAt).toISOString(),
            description_md: description,
            target_image_tag: targetTag.trim() === '' ? null : targetTag.trim(),
            kind,
            reason: reason.trim() === '' ? null : reason.trim(),
          },
        },
      );
      onCreated();
      if (r.warnings.length > 0) {
        // Stay on the form so the operator actually reads the warning (< 24h
        // lead time, overlapping window). The list already refreshed via
        // onCreated, so the new window is visible below.
        setWarnings(r.warnings);
        return;
      }
      navigate(`/maintenance/${r.window.id}`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        const body = (err.body ?? {}) as PolicyRejection;
        const key = readErrorKey(err.body);
        if (
          key === 'outside_maintenance_window' ||
          key === 'below_min_notice' ||
          key === 'integrity_reason_required'
        ) {
          // OCP-527: a contractual rejection, not a typo. Show what 5.2 allows
          // and offer the next slot that would be accepted.
          setRejection(body);
        } else {
          setError('Invalid input. The target version must be a plain X.Y.Z tag (no v-prefix) at or above the min-web floor.');
        }
      } else {
        setError(err instanceof ApiError ? `Could not create window (HTTP ${err.status}).` : 'Could not create window.');
      }
    } finally {
      setSubmitting(false);
    }
  }, [scheduledAt, description, targetTag, kind, reason, navigate, onCreated, policyZone]);

  const policy = scope?.policy ?? null;

  return (
    <section className="mt-6 rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 shadow-sm">
      <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Schedule a window</h2>

      {policy && <PolicyPanel policy={policy} />}

      <fieldset className="mt-4">
        <legend className="text-sm text-gray-700 dark:text-gray-300">Kind (AGB V4 5.2)</legend>
        <div className="mt-2 flex flex-wrap gap-4">
          {(Object.keys(KIND_LABEL) as MaintenanceKind[]).map((k) => (
            <label key={k} className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="maintenance-kind"
                value={k}
                checked={kind === k}
                onChange={() => setKind(k)}
                aria-label={KIND_LABEL[k]}
              />
              <span className="text-gray-700 dark:text-gray-300">{KIND_LABEL[k]}</span>
            </label>
          ))}
        </div>
        <p className="mt-2 text-xs text-gray-500">{KIND_HELP[kind]}</p>
      </fieldset>

      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <label className="block text-sm">
          <span className="text-gray-700 dark:text-gray-300">Scheduled time ({policyZone} wall clock, {zoneLabel(scheduledAt || undefined, policyZone)})</span>
          <input
            type="datetime-local"
            value={scheduledAt}
            onChange={(e) => setScheduledAt(e.target.value)}
            aria-label="Scheduled time"
            className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none"
          />
        </label>
        <label className="block text-sm">
          <span className="text-gray-700 dark:text-gray-300">Target version (optional)</span>
          <input
            type="text"
            value={targetTag}
            onChange={(e) => setTargetTag(e.target.value)}
            placeholder={scope?.rolling_image_tag ?? 'e.g. 0.142.0'}
            aria-label="Target version"
            autoComplete="off"
            className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1.5 font-mono text-sm focus:border-brand-500 focus:outline-none"
          />
          <span className="mt-1 block text-xs text-gray-500">
            Leave blank to roll onto the current fleet tag ({scope?.rolling_image_tag ?? '…'}).
          </span>
        </label>
      </div>

      <label className="mt-4 block text-sm">
        <span className="text-gray-700 dark:text-gray-300">
          Reason{reasonRequired ? '' : ' (optional)'}
        </span>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={2}
          aria-label="Reason"
          placeholder={
            reasonRequired
              ? 'Why does system integrity require this outside the contractual windows?'
              : 'Internal note on why this window carries its kind.'
          }
          className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none"
        />
        <span className="mt-1 block text-xs text-gray-500">
          {reasonRequired
            ? 'Required for integrity maintenance. It is written to the audit log AND shown to the customer in the maintenance e-mail (AGB V4 5.2 grants the customer no claim to a notice; we disclose integrity work anyway).'
            : 'Recorded on the window and in the audit log. Only an integrity reason is shown to the customer.'}
        </span>
      </label>

      <label className="mt-4 block text-sm">
        <span className="text-gray-700 dark:text-gray-300">Description (Markdown, shown to tenants)</span>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={4}
          aria-label="Description"
          placeholder={'## Scheduled maintenance\nWe are updating your instance. Expected downtime ~5 min.'}
          className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1.5 font-mono text-sm focus:border-brand-500 focus:outline-none"
        />
        <span className="mt-1 block text-xs text-gray-500" data-testid="maintenance-description-hint">
          The tenant-facing part of the pre-notify and all-clear mail. Do not put the version number here - tenants
          get the description only, never the tag. Markdown: lists, links, quotes, *emphasis* and ## headings render,
          raw HTML is shown as text. Write a literal asterisk as \* (2*3*4 would turn into emphasis).
        </span>
      </label>
      <div className="mt-2">
        <button
          type="button"
          onClick={() => void previewMails()}
          disabled={description.trim() === '' || previewing}
          data-testid="maintenance-mail-preview"
          className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1 text-xs font-medium text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
        >
          {previewing ? 'Rendering...' : 'Preview mails'}
        </button>
      </div>
      {mailPreview && (
        <div className="mt-3 space-y-3" data-testid="maintenance-mail-preview-result">
          <p className="text-[11px] text-gray-500">
            Rendered for {mailPreview.tenant_url}
            {mailPreview.per_tenant_url ? ' (example - each tenant gets its own address)' : ''}. Whether a window mails at all
            depends on its kind and slot.
          </p>
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            <MailRenderPreview mail={mailPreview.pre_notify.de} label="Pre-notify (de)" testId="maintenance-preview-pre-de" />
            <MailRenderPreview mail={mailPreview.pre_notify.en} label="Pre-notify (en)" testId="maintenance-preview-pre-en" />
            <MailRenderPreview mail={mailPreview.all_clear.de} label="All-clear (de)" testId="maintenance-preview-clear-de" />
            <MailRenderPreview mail={mailPreview.all_clear.en} label="All-clear (en)" testId="maintenance-preview-clear-en" />
          </div>
        </div>
      )}

      {scope && (
        <div className="mt-4 rounded border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/40 px-3 py-2 text-sm text-gray-600 dark:text-gray-400">
          Scope preview:{' '}
          <span className="font-medium text-gray-900 dark:text-gray-100">{scope.eligible.count}</span>{' '}
          latest-channel tenant(s) will be rolled;{' '}
          <span className="font-medium text-gray-900 dark:text-gray-100">{scope.pinned.count}</span>{' '}
          pinned tenant(s) skipped.
        </div>
      )}

      {error && (
        <div
          role="alert"
          className="mt-3 rounded border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300"
        >
          {error}
        </div>
      )}
      {rejection && (
        <div
          role="alert"
          className="mt-3 rounded border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300"
        >
          <p className="font-semibold">Rejected: this schedule breaks AGB V4 5.2.</p>
          <ul className="mt-1 list-disc pl-5">
            {(rejection.violations ?? [rejection.error ?? '']).filter(Boolean).map((v) => (
              <li key={v}>
                {v === 'outside_maintenance_window'
                  ? 'Outside the contractual maintenance windows (AGB V4 5.2). Routine maintenance runs in the configured windows - for work that cannot wait use Integrity with a reason.'
                  : v === 'below_min_notice'
                    ? `Less than the contractual advance notice${
                        rejection.policy ? ` of ${rejection.policy.min_notice_hours}h` : ''
                      } (a slot configured with a notice duty).`
                    : v === 'integrity_reason_required'
                      ? 'An integrity window needs a reason.'
                      : v}
              </li>
            ))}
          </ul>
          {rejection.next_compliant_at && (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <span>
                Next valid slot:{' '}
                <span className="font-medium">
                  {fmtDateTime(rejection.next_compliant_at, { timeZone: policyZone })}
                </span>
              </span>
              <button
                type="button"
                onClick={() => {
                  setScheduledAt(toDatetimeLocalValue(rejection.next_compliant_at!, policyZone));
                  setRejection(null);
                }}
                className="rounded border border-red-400 dark:border-red-600 px-2 py-0.5 text-xs font-semibold hover:bg-red-100 dark:hover:bg-red-900/50"
              >
                Use this slot
              </button>
            </div>
          )}
        </div>
      )}
      {warnings.length > 0 && (
        <div className="mt-3 rounded border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/30 px-3 py-2 text-sm text-amber-800 dark:text-amber-300">
          {warnings.map((w) => (
            <div key={w}>⚠ {w}</div>
          ))}
        </div>
      )}

      <div className="mt-4">
        <button
          type="button"
          onClick={() => void submit()}
          disabled={!canSubmit}
          className="rounded bg-brand-500 px-4 py-1.5 text-sm font-semibold text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {submitting ? 'Scheduling…' : 'Schedule window'}
        </button>
      </div>
    </section>
  );
}

/**
 * OCP-527: what AGB V4 5.2 currently permits, straight from the live settings
 * (`maintenance.windows` / `maintenance.timezone` / `maintenance.min_notice_hours`).
 * Shown above the form so the operator sees the constraint before picking a
 * time rather than after a rejection.
 *
 * OCP-537: routine maintenance lives in the configured windows and owes
 * no individual notice there, so the panel says that plainly instead of
 * advertising a notice period that does not apply. A slot an operator adds
 * WITH a notice duty is marked, so the two cases never look alike.
 */
function PolicyPanel({ policy }: { policy: MaintenancePolicyView }) {
  const anyNoticeSlot = policy.windows.some((w) => w.requires_notice);
  return (
    <div className="mt-4 rounded border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/40 px-3 py-2 text-sm text-gray-600 dark:text-gray-400">
      <p className="font-medium text-gray-900 dark:text-gray-100">
        Contractual maintenance windows (AGB V4 5.2)
      </p>
      <ul className="mt-1 list-disc pl-5">
        {policy.windows.map((w) => (
          <li key={w.label} className="tabular-nums">
            {w.label}
            {w.requires_notice ? (
              <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-xs font-semibold tabular-nums text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                {policy.min_notice_hours}h notice required
              </span>
            ) : (
              <span className="ml-2 rounded bg-green-100 px-1.5 py-0.5 text-xs font-semibold text-green-800 dark:bg-green-900/40 dark:text-green-300">
                no individual notice required
              </span>
            )}
          </li>
        ))}
      </ul>
      <p className="mt-1">
        Times are {policy.timezone} wall clock (DST-aware). Routine maintenance runs in the
        windows above and needs no individual advance notice there (AGB V4 5.2) - a short
        lead is accepted. Customers always see an in-product notice while it runs.
        {anyNoticeSlot && (
          <>
            {' '}
            A slot marked above as notice-bearing needs{' '}
            <span className="font-medium text-gray-900 dark:text-gray-100">
              {policy.min_notice_hours}h
            </span>{' '}
            of lead.
          </>
        )}
      </p>
      <p className="mt-1">
        Outside the windows there is no routine path. Interrupting work that cannot wait for the
        next window must be scheduled as <span className="font-medium">Integrity</span> (AGB V4 5.2:
        security, integrity or an error that materially impairs use) with a reason - it is
        audited and disclosed to the customer.
        {policy.next_compliant_at && (
          <>
            {' '}
            Earliest compliant slot right now:{' '}
            <span className="font-medium text-gray-900 dark:text-gray-100">
              {fmtDateTime(policy.next_compliant_at, { timeZone: policy.timezone })}
            </span>
            .
          </>
        )}
      </p>
    </div>
  );
}

/** OCP-527: kind badge. Integrity is deliberately loud - it means we went outside the contractual windows. */
function KindPill({ kind }: { kind: MaintenanceKind }) {
  const cls =
    kind === 'integrity'
      ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300'
      : kind === 'non_interrupting'
        ? 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300'
        : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';
  return (
    <span
      title={KIND_HELP[kind]}
      className={`inline-flex items-center rounded px-1.5 py-0.5 text-xs font-semibold ${cls}`}
    >
      {KIND_LABEL[kind]}
    </span>
  );
}

// ---- detail page ---------------------------------------------------

export function AdminMaintenanceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [window, setWindow] = useState<MaintenanceWindow | null>(null);
  const [events, setEvents] = useState<TenantEvent[]>([]);
  const [mailLocales, setMailLocales] = useState<MailLocales | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const r = await api<{ window: MaintenanceWindow; events: TenantEvent[]; mail_locales?: MailLocales }>(
        `/v1/admin/maintenance-windows/${id}`,
      );
      setWindow(r.window);
      setEvents(r.events);
      setMailLocales(r.mail_locales ?? null);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) setNotFound(true);
      else setError(err instanceof ApiError ? `Could not load window (HTTP ${err.status}).` : 'Could not load window.');
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  // Live progress polling: while the window is pending or running the per-
  // tenant events are still moving, so poll every 3s. Stop once the window is
  // in a terminal state (or awaiting-confirm, where the roll itself is done).
  const polling = window !== null && (window.status === 'pending' || window.status === 'running');
  useEffect(() => {
    if (!polling) return;
    const timer = setInterval(() => {
      void load();
    }, 3000);
    return () => clearInterval(timer);
  }, [polling, load]);

  if (notFound) {
    return (
      <div className="mx-auto max-w-4xl">
        <Link to="/maintenance" className="text-sm text-brand-500 hover:text-brand-600">
          ← Back to maintenance windows
        </Link>
        <h1 className="mt-2 text-2xl font-semibold tracking-tight">Window not found</h1>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl">
      <Link to="/maintenance" className="text-sm text-brand-500 hover:text-brand-600">
        ← Back to maintenance windows
      </Link>

      {error && (
        <div
          role="alert"
          className="mt-3 rounded border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300"
        >
          {error}
        </div>
      )}

      {window === null && !error ? (
        <div className="mt-6 rounded-lg border border-dashed border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 p-6 text-sm text-gray-500">
          Loading…
        </div>
      ) : window ? (
        <>
          <div className="mt-2 flex items-baseline justify-between gap-4">
            <div>
              <h1 className="text-2xl font-semibold tracking-tight">Maintenance window</h1>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                {fmtDateTime(window.scheduled_at)} · scheduled by {window.created_by_email}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <KindPill kind={window.kind ?? 'routine'} />
              <StatusPill status={window.status} />
            </div>
          </div>

          <section className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
            <InfoCard label="Target version" value={window.target_image_tag ?? 'current fleet tag'} mono />
            {/* OCP-590: non_interrupting windows send no tenant mail (Terms
                5.2) - the stamps then mean "phase processed", not "mails
                went out". Label them honestly so the operator never reads a
                mail-free window as a mailed one. */}
            <InfoCard
              label={window.kind === 'non_interrupting' ? 'Notify phase (no mail - 5.2)' : 'Notification sent'}
              value={window.notification_sent_at ? fmtDateTime(window.notification_sent_at) : 'not yet'}
            />
            <InfoCard
              label={window.kind === 'non_interrupting' ? 'All-clear phase (no mail - 5.2)' : 'All-clear sent'}
              value={window.all_clear_sent_at ? fmtDateTime(window.all_clear_sent_at) : 'not yet'}
            />
          </section>

          {/* OCP-527: an out-of-window maintenance must never be indistinguishable
              from an ordinary one - the recorded justification is shown here and
              in the customer's e-mail. */}
          {(window.kind ?? 'routine') !== 'routine' && (
            <section className="mt-6">
              <h2 className="mb-2 text-base font-semibold text-gray-900 dark:text-gray-100">
                AGB V4 5.2 classification
              </h2>
              <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 text-sm text-gray-700 dark:text-gray-300">
                <p>{KIND_HELP[window.kind ?? 'routine']}</p>
                {window.kind_reason && (
                  <p className="mt-2">
                    <span className="font-medium text-gray-900 dark:text-gray-100">Reason: </span>
                    {window.kind_reason}
                  </p>
                )}
              </div>
            </section>
          )}

          <section className="mt-6">
            <h2 className="mb-2 text-base font-semibold text-gray-900 dark:text-gray-100">Description</h2>
            <div className="whitespace-pre-wrap rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 font-mono text-sm text-gray-700 dark:text-gray-300">
              {window.description_md}
            </div>
          </section>

          <WindowActions window={window} onChanged={() => void load()} />

          <section className="mt-8">
            <div className="mb-2 flex items-center justify-between">
              <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Per-tenant progress</h2>
              {polling && (
                <span className="text-xs text-gray-500" role="status">
                  live · refreshing every 3s
                </span>
              )}
            </div>
            {window.counts && <ProgressBar counts={window.counts} />}
            {/* OCP-593 - which language the notices went out in, and who got
                the English fallback because their locale has no template. */}
            {mailLocales && events.length > 0 && (
              <p className="mt-2 text-xs text-gray-600 dark:text-gray-400" data-testid="maintenance-mail-locales">
                Mail languages: {mailLocales.de} de · {mailLocales.en} en
                {mailLocales.fallback.length > 0 && (
                  <span className="ml-1 text-amber-800 dark:text-amber-300">
                    · en fallback for {mailLocales.fallback.map((f) => `${f.subdomain ?? f.tenant_id.slice(0, 8)} (${f.locale})`).join(', ')}
                  </span>
                )}
              </p>
            )}
            <TenantEventTable events={events} />
          </section>
        </>
      ) : null}
    </div>
  );
}

function WindowActions({ window, onChanged }: { window: MaintenanceWindow; onChanged: () => void }) {
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState<'cancel' | 'confirm-done' | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cancel = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      await api(`/v1/admin/maintenance-windows/${window.id}`, { method: 'DELETE' });
      onChanged();
      navigate('/maintenance');
    } catch (err) {
      setError(err instanceof ApiError ? `Could not cancel (HTTP ${err.status}).` : 'Could not cancel.');
    } finally {
      setBusy(false);
      setConfirming(null);
    }
  }, [window.id, navigate, onChanged]);

  const confirmDone = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      await api(`/v1/admin/maintenance-windows/${window.id}/confirm-done`, { method: 'POST' });
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? `Could not confirm (HTTP ${err.status}).` : 'Could not confirm.');
    } finally {
      setBusy(false);
      setConfirming(null);
    }
  }, [window.id, onChanged]);

  const canCancel = window.status === 'pending';
  const canConfirm = window.status === 'completed_pending_confirm';
  if (!canCancel && !canConfirm) return null;

  return (
    <section className="mt-6 rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/40 p-4">
      {error && (
        <div
          role="alert"
          className="mb-3 rounded border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300"
        >
          {error}
        </div>
      )}

      {canCancel &&
        (confirming === 'cancel' ? (
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <span className="text-gray-700 dark:text-gray-300">Cancel this pending window?</span>
            <button
              type="button"
              onClick={() => void cancel()}
              disabled={busy}
              className="rounded bg-red-600 px-3 py-1 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
            >
              {busy ? 'Cancelling…' : 'Yes, cancel'}
            </button>
            <button
              type="button"
              onClick={() => setConfirming(null)}
              className="text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
            >
              Keep it
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setConfirming('cancel')}
            className="rounded border border-red-300 dark:border-red-700 bg-white dark:bg-gray-900 px-3 py-1.5 text-sm font-medium text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30"
          >
            Cancel window
          </button>
        ))}

      {canConfirm &&
        (confirming === 'confirm-done' ? (
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <span className="text-gray-700 dark:text-gray-300">
              Send the all-clear email to every rolled tenant? This cannot be undone.
            </span>
            <button
              type="button"
              onClick={() => void confirmDone()}
              disabled={busy}
              className="rounded bg-brand-500 px-3 py-1 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-50"
            >
              {busy ? 'Sending…' : 'Yes, send all-clear'}
            </button>
            <button
              type="button"
              onClick={() => setConfirming(null)}
              className="text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
            >
              Not yet
            </button>
          </div>
        ) : (
          <div>
            <button
              type="button"
              onClick={() => setConfirming('confirm-done')}
              className="rounded bg-brand-500 px-4 py-1.5 text-sm font-semibold text-white hover:bg-brand-600"
            >
              Confirm done (send all-clear)
            </button>
            <p className="mt-1 text-xs text-gray-500">
              The technical roll is finished. This fires the &ldquo;update complete&rdquo; email -
              hold it until you are sure no hotfix is needed.
            </p>
          </div>
        ))}
    </section>
  );
}

function TenantEventTable({ events }: { events: TenantEvent[] }) {
  if (events.length === 0) {
    return (
      <div className="mt-3 rounded-lg border border-dashed border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 p-6 text-sm text-gray-500">
        No per-tenant events yet. They appear once the notify job has run.
      </div>
    );
  }
  return (
    <div className="mt-3 overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 dark:bg-gray-800 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
          <tr>
            <th className="px-3 py-2">Tenant</th>
            <th className="px-3 py-2">Status</th>
            <th className="px-3 py-2">Started</th>
            <th className="px-3 py-2">Completed</th>
            <th className="px-3 py-2">Error</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
          {events.map((e) => (
            <tr key={e.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/60">
              <td className="px-3 py-2 font-medium text-gray-800 dark:text-gray-200">
                {e.subdomain ?? e.tenant_id.slice(0, 8)}
              </td>
              <td className="px-3 py-2">
                <EventStatusPill status={e.status} />
              </td>
              <td className="whitespace-nowrap px-3 py-2 tabular-nums text-gray-500">
                {e.started_at ? fmtTime(e.started_at) : '-'}
              </td>
              <td className="whitespace-nowrap px-3 py-2 tabular-nums text-gray-500">
                {e.completed_at ? fmtTime(e.completed_at) : '-'}
              </td>
              <td className="px-3 py-2 text-red-600 dark:text-red-400">{e.error_message ?? ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---- autonomous cycle panel (OCP-505) ------------------------------

/**
 * Read-only status of the autonomous nightly (OCP-758) security-patch + controlled-reboot
 * cycle. Knobs live in Admin Settings (fleet.*); this shows enabled/schedule/
 * next run + the last run summary + the auto windows the cycle opened. Fully
 * defensive: any load failure (or an unconfigured endpoint) renders nothing so
 * it never blocks the maintenance page.
 */
export function AutoMaintenancePanel() {
  const [data, setData] = useState<AutoMaintenanceStatus | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const r = await api<AutoMaintenanceStatus>('/v1/admin/fleet/auto-maintenance');
        if (r && typeof r.schedule === 'string') setData(r);
      } catch {
        // Panel is a nicety - a failure must not block the page.
      }
    })();
  }, []);

  if (!data) return null;

  return (
    <section className="mt-6 rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
          Autonomous fleet maintenance
        </h2>
        <span
          className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-semibold uppercase tracking-wide ring-1 ring-inset ${
            data.enabled
              ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 ring-green-200 dark:ring-green-800'
              : 'bg-gray-100 dark:bg-gray-800 text-gray-500 ring-gray-200 dark:ring-gray-700'
          }`}
        >
          {data.enabled ? 'Enabled' : 'Off (kill-switch)'}
        </span>
      </div>
      <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
        Nightly OS security patch and host-agent update of every host (OCP-758); a host is rebooted only
        when its kernel requires it - also a reboot left pending by a manual patch - inside an
        auto-created + auto-confirmed maintenance window so its tenants are notified exactly when they
        actually go down. The proxy refresh runs on the weekly day only; tenant version updates run every night (OCP-840). Change the knobs under Settings &rarr; Worker (fleet.*).
      </p>

      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <InfoCard label={`Schedule (cron, ${data.schedule_timezone ?? 'UTC'} wall clock)`} value={data.schedule} mono />
        <InfoCard
          label="Next run"
          value={data.next_run_at ? fmtDateTime(data.next_run_at) : 'unknown'}
        />
        <InfoCard label="Notify lead" value={`${data.window_lead_minutes} min`} />
        <InfoCard label="Reboot cap / run" value={String(data.max_reboots_per_run)} />
      </div>

      {data.last_run && (
        <div className="mt-4 rounded border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/40 px-3 py-2 text-sm text-gray-600 dark:text-gray-400">
          Last patch/reboot run {fmtDateTime(data.last_run.at)}:{' '}
          <code className="font-mono text-xs">{JSON.stringify(data.last_run.summary)}</code>
        </div>
      )}

      {/* OCP-508 - Phase B: autonomous tenant version convergence. */}
      <div className="mt-5 border-t border-gray-200 dark:border-gray-800 pt-4">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Tenant version convergence (Phase B)
          </h3>
          <span
            className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-semibold uppercase tracking-wide ring-1 ring-inset ${
              data.update_tenants
                ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 ring-green-200 dark:ring-green-800'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-500 ring-gray-200 dark:ring-gray-700'
            }`}
          >
            {data.update_tenants ? 'Enabled' : 'Off (kill-switch)'}
          </span>
        </div>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          Every night, after the patch/reboot half, each latest-channel unpinned tenant&apos;s live
          version is read from its own /api/version; the ones behind are rolled onto the verified core
          release (fleet.verified_core_release, only when it is a stable registry tag - never a newer
          tag) - canaries (demo + allowlist) first + health-gated, then customers one at a time,
          health-gated, up to the cap per night (anything over it goes the next night). A tenant
          already on the release is recorded, never restarted; an unreachable one is not rolled.
          Pinned tenants are never touched. These windows are non-interrupting and send no customer
          mail.
        </p>
        <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
          <InfoCard
            label="Verified core release"
            value={data.verified_core_release ?? 'none - no roll'}
            mono
          />
          <InfoCard label="Customer updates / night" value={String(data.max_tenant_updates_per_run)} />
          <InfoCard
            label="Canary subdomains"
            value={
              (data.canary_subdomains ?? []).length > 0
                ? (data.canary_subdomains ?? []).join(', ')
                : 'demo only'
            }
            mono
          />
        </div>
        {data.last_convergence && (
          <div className="mt-3 rounded border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/40 px-3 py-2 text-sm text-gray-600 dark:text-gray-400">
            Last convergence run {fmtDateTime(data.last_convergence.at)}:{' '}
            <code className="font-mono text-xs">{JSON.stringify(data.last_convergence.summary)}</code>
          </div>
        )}
      </div>

      {data.recent_windows.length > 0 && (
        <div className="mt-4">
          <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">
            Recent auto windows
          </h3>
          <ul className="space-y-1 text-sm">
            {data.recent_windows.map((w) => (
              <li key={w.id} className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                <Link to={`/maintenance/${w.id}`} className="font-medium text-brand-500 hover:text-brand-600">
                  {w.hostname ?? w.id.slice(0, 8)}
                </Link>
                <span className="text-xs text-gray-500">{w.status}</span>
                <span className="text-xs text-gray-400">
                  {w.scheduled_at ? fmtDateTime(w.scheduled_at) : ''}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

// ---- shared bits ---------------------------------------------------

function StatusPill({ status }: { status: WindowStatus }) {
  const cls: Record<WindowStatus, string> = {
    pending: 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 ring-gray-200 dark:ring-gray-700',
    running: 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 ring-blue-200 dark:ring-blue-800',
    completed_pending_confirm:
      'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 ring-amber-200 dark:ring-amber-800',
    completed: 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 ring-green-200 dark:ring-green-800',
    cancelled: 'bg-gray-100 dark:bg-gray-800 text-gray-500 ring-gray-200 dark:ring-gray-700',
  };
  return (
    <span
      className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-semibold uppercase tracking-wide ring-1 ring-inset ${cls[status]}`}
    >
      {STATUS_LABEL[status]}
    </span>
  );
}

function EventStatusPill({ status }: { status: EventStatus }) {
  const cls: Record<EventStatus, string> = {
    skipped_pinned: 'bg-gray-100 dark:bg-gray-800 text-gray-500',
    notified: 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400',
    rolling: 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
    completed: 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300',
    failed: 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300',
    skipped_deploy_in_flight: 'bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300',
  };
  return (
    <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium ${cls[status]}`}>
      {EVENT_STATUS_LABEL[status]}
    </span>
  );
}

function ProgressSummary({ counts }: { counts: WindowCounts }) {
  const done = counts.completed + counts.failed + counts.skipped_pinned + (counts.skipped_deploy_in_flight ?? 0);
  return (
    <span className="tabular-nums">
      {done}/{counts.total} resolved
      {counts.failed > 0 && <span className="ml-1 text-red-600 dark:text-red-400">({counts.failed} failed)</span>}
    </span>
  );
}

function ProgressBar({ counts }: { counts: WindowCounts }) {
  const pct = useMemo(() => {
    if (counts.total === 0) return 0;
    const resolved = counts.completed + counts.failed + counts.skipped_pinned + (counts.skipped_deploy_in_flight ?? 0);
    return Math.round((resolved / counts.total) * 100);
  }, [counts]);
  return (
    <div>
      <div className="flex items-center justify-between text-xs text-gray-500">
        <span>
          {counts.completed} completed · {counts.failed} failed · {counts.skipped_pinned} skipped ·{' '}
          {counts.skipped_deploy_in_flight ? <>{counts.skipped_deploy_in_flight} skipped (deploy in flight) ·{' '}</> : null}
          {counts.rolling} rolling · {counts.notified} notified
        </span>
        <span className="tabular-nums">{pct}%</span>
      </div>
      <div className="mt-1 h-2 w-full overflow-hidden rounded bg-gray-200 dark:bg-gray-800">
        <div
          className={`h-full ${counts.failed > 0 ? 'bg-amber-500' : 'bg-brand-500'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function InfoCard({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-3 shadow-sm">
      <div className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</div>
      <div className={`mt-1 text-sm font-medium text-gray-900 dark:text-gray-100 ${mono ? 'font-mono' : ''}`}>
        {value}
      </div>
    </div>
  );
}
